import { useEffect, useRef, useState } from "react";
import { BrowserMultiFormatReader, BrowserCodeReader } from "@zxing/browser";
import { Button } from "@/components/ui/button";
import { Camera, CameraOff, RefreshCcw } from "lucide-react";
import { toast } from "sonner";

/**
 * Mobile-first camera-based QR scanner.
 * Calls onDetect(text) once a code is read; auto-pauses afterwards until reset.
 */
export default function CameraScanner({ onDetect, autoPauseAfterDetect = true }) {
  const videoRef = useRef(null);
  const readerRef = useRef(null);
  const controlsRef = useRef(null);
  const [running, setRunning] = useState(false);
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState("");
  const [error, setError] = useState("");

  // Enumerate cameras (rear preferred)
  useEffect(() => {
    (async () => {
      try {
        const cams = await BrowserCodeReader.listVideoInputDevices();
        setDevices(cams);
        const rear = cams.find((c) => /back|rear|environment/i.test(c.label));
        setDeviceId((rear || cams[0])?.deviceId || "");
      } catch (e) {
        setError("Camera list unavailable: " + e.message);
      }
    })();
  }, []);

  const start = async () => {
    setError("");
    if (!deviceId) { toast.error("No camera detected"); return; }
    try {
      const reader = new BrowserMultiFormatReader();
      readerRef.current = reader;
      const controls = await reader.decodeFromVideoDevice(deviceId, videoRef.current, (result, err) => {
        if (result) {
          const text = result.getText();
          onDetect?.(text);
          if (autoPauseAfterDetect) {
            try { controls.stop(); } catch { /* */ }
            setRunning(false);
          }
        }
      });
      controlsRef.current = controls;
      setRunning(true);
    } catch (e) {
      setError(e.message || "Camera permission denied");
      toast.error("Camera permission denied — use Manual mode or check browser settings.");
    }
  };

  const stop = () => {
    try { controlsRef.current?.stop?.(); } catch { /* */ }
    setRunning(false);
  };

  // Cleanup on unmount
  useEffect(() => () => stop(), []);

  return (
    <div data-testid="camera-scanner" className="rounded-sm border border-slate-200 bg-black p-2 text-white">
      <div className="relative aspect-[4/3] overflow-hidden rounded-sm bg-slate-900">
        <video ref={videoRef} autoPlay muted playsInline className="h-full w-full object-cover" />
        {!running && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/60 p-6 text-center">
            <Camera size={32} className="text-white/70" />
            <p className="text-sm text-white/80">{error || "Camera ready. Point at the QR/barcode on the returned envelope."}</p>
            <Button onClick={start} data-testid="cam-start-btn" disabled={!deviceId} className="rounded-sm bg-[#002FA7] hover:bg-[#00227A]">
              <Camera size={14} className="mr-1.5" />Start scanning
            </Button>
          </div>
        )}
        {/* viewport overlay */}
        {running && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="h-44 w-44 rounded-sm border-2 border-emerald-400 shadow-[0_0_0_9999px_rgba(0,0,0,0.45)]" />
          </div>
        )}
      </div>
      <div className="mt-2 flex items-center gap-2 px-1 text-xs">
        {devices.length > 1 && (
          <select
            value={deviceId}
            onChange={(e) => { stop(); setDeviceId(e.target.value); }}
            data-testid="cam-device-select"
            className="rounded-sm bg-slate-800 px-2 py-1 text-white"
          >
            {devices.map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label || d.deviceId.slice(0, 8)}</option>)}
          </select>
        )}
        <span className="text-slate-300">{running ? "Scanning…" : "Idle"}</span>
        <div className="ml-auto flex gap-2">
          {running ? (
            <Button onClick={stop} size="sm" variant="outline" className="rounded-sm border-slate-700 bg-slate-800 text-white hover:bg-slate-700" data-testid="cam-stop-btn"><CameraOff size={12} className="mr-1" />Stop</Button>
          ) : (
            deviceId ? <Button onClick={start} size="sm" variant="outline" className="rounded-sm border-slate-700 bg-slate-800 text-white hover:bg-slate-700" data-testid="cam-restart-btn"><RefreshCcw size={12} className="mr-1" />Scan again</Button> : null
          )}
        </div>
      </div>
    </div>
  );
}
